import { Box, Center, CenterProps, Flex, Text } from '@chakra-ui/react'
import { useFilePath } from '@hb/react'
import {
  AttachmentShortcut,
  DataTemplateAttachment,
  FieldTypes,
  FileDBValue,
  FileTemplateAttachment,
  getInlineFieldSize,
  getShortcutValue,
  InlineAttachmentPlacement,
  InputTemplateAttachment,
  PIXEL_PDF_UNITS_SCALE,
  ShapeTemplateAttachment,
  TemplateAttachment,
} from '@hb/shared'
import { motion, MotionProps } from 'framer-motion'
import {
  ForwardRefExoticComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFile, UseFileData } from '../../hooks/backend/storage/downloadFile'
import errorIcon from '../../icons/error_red.svg'
import imageIcon from '../../icons/image.svg'
import { OrientationOverlay } from '../Gestures/OrientationOverlay'
import { ResizeProps } from '../Gestures/types'
import { TemplateFieldElementView } from '../RichText/Elements/TemplateFieldElementView'
import { fontFamilyFromFontId } from '../RichText/TemplateToolbar/utils'
import { useExternalPdfTemplateEditor } from '../Templates/ExternalPdf/context'
import { EditMode, useTemplateData } from '../Templates/contexts/data'
import { useEditMode } from '../Templates/contexts/editMode'
import { LoadingImage } from '../shared/LoadingImage'
import {
  defaultAttachmentContainerStyle,
  getAttachmentFieldStyle,
  inputSizeFromFontSize,
} from './attachmentFieldStyles'
import { usePdfView } from './context'
const MCenter = motion.create(
  Center as ForwardRefExoticComponent<CenterProps>,
) as ForwardRefExoticComponent<Omit<CenterProps, 'style'> & MotionProps>

interface ContainerProps {
  pageWidth: number
  pageHeight: number
  pageScale: number
}

const InlinePlacementEdit = ({
  children,
  attachment,
  selected,
  pageHeight,
  pageWidth,
  pageScale,
  onDragStart,
  onDragEnd,
}: PropsWithChildren<
  {
    attachment: TemplateAttachment<InlineAttachmentPlacement>
    selected: boolean
    onDragStart: () => void
    onDragEnd: () => void
  } & ContainerProps
>) => {
  const { id: attachmentId, placement } = attachment
  const { scale } = placement.orientation
  // Radio inputs resize by an explicit width/height box (like data/shape) rather than by scale.
  const isRadio = attachment.type === 'input' && attachment.field.type === FieldTypes.DROPDOWN
  const { onAttachmentSelect, updateAttachment, deleteAttachment } = useExternalPdfTemplateEditor()

  const [isDragActive, setIsDragActive] = useState(false)
  const handleDragStart = useCallback(() => {
    setIsDragActive(true)
    onDragStart()
  }, [onDragStart])
  const handleDragEnd = useCallback(() => {
    setIsDragActive(false)
    onDragEnd()
  }, [onDragEnd])

  const size = useMemo(() => {
    if (attachment.type === 'data' || attachment.type === 'shape') {
      return [attachment.width, attachment.height]
    }
    const base = getInlineFieldSize(attachment)
    return [base[0] * scale[0], base[1] * scale[1]]
  }, [attachment, scale])

  const bounds = useMemo(() => {
    // Safety check: ensure size values are positive

    const safeWidth = Math.max(size[0], 1)
    const safeHeight = Math.max(size[1], 1)
    const minX = safeWidth / 2
    const minY = safeHeight / 2
    const maxX = pageWidth - safeWidth / 2
    const maxY = pageHeight - safeHeight / 2
    return { minX, minY, maxX, maxY }
  }, [pageHeight, pageWidth, size])
  const onMove = useCallback(
    (pos: [number, number]) => {
      const newX = Math.min(Math.max(pos[0], bounds.minX), bounds.maxX)
      const newY = Math.min(Math.max(pos[1], bounds.minY), bounds.maxY)
      updateAttachment(attachmentId, {
        ...attachment,
        placement: {
          ...attachment.placement,
          orientation: {
            ...attachment.placement.orientation,
            position: [newX, newY],
          },
        },
      })
    },
    [attachment, attachmentId, updateAttachment, bounds],
  )

  // check if position is within bounds, if not, constrain it
  // skip during active drag to prevent snapping while resize bounds are updating
  useEffect(() => {
    if (isDragActive) return
    if (
      placement.orientation.position[0] < bounds.minX ||
      placement.orientation.position[0] > bounds.maxX ||
      placement.orientation.position[1] < bounds.minY ||
      placement.orientation.position[1] > bounds.maxY
    ) {
      const newX = Math.min(Math.max(placement.orientation.position[0], bounds.minX), bounds.maxX)
      const newY = Math.min(Math.max(placement.orientation.position[1], bounds.minY), bounds.maxY)
      updateAttachment(attachmentId, {
        ...attachment,
        placement: {
          ...attachment.placement,
          orientation: {
            ...attachment.placement.orientation,
            position: [newX, newY],
          },
        },
      })
    }
  }, [
    attachment,
    attachmentId,
    updateAttachment,
    bounds,
    placement.orientation.position,
    isDragActive,
  ])

  const resizeMode = useMemo(
    () =>
      attachment.type === 'data' || attachment.type === 'shape' || isRadio ? 'resize' : 'scale',
    [attachment.type, isRadio],
  )
  const onResize = useCallback(
    (updated: [number, number]) => {
      if (attachment.type === 'data' || attachment.type === 'shape' || isRadio) {
        updateAttachment(attachmentId, {
          ...attachment,
          width: updated[0],
          height: updated[1],
        })
      } else {
        updateAttachment(attachmentId, {
          ...attachment,
          placement: {
            ...attachment.placement,
            orientation: {
              ...attachment.placement.orientation,
              scale: updated,
            },
          },
        })
      }
    },
    [attachment, attachmentId, updateAttachment, isRadio],
  )

  const resize = useMemo<ResizeProps>(
    () => ({
      mode: resizeMode,
      value:
        attachment.type === 'data' || attachment.type === 'shape'
          ? [attachment.width, attachment.height]
          : isRadio
            ? (getInlineFieldSize(attachment) as [number, number])
            : scale,
      onResize,
      // Radio boxes resize freely; other inputs keep their aspect ratio while scaling.
      constrainAspectRatio:
        (attachment.type === 'input' && !isRadio) ||
        (attachment.type === 'shape' &&
          (attachment.shapeType === 'circle' || attachment.shapeType === 'square')),
      min: resizeMode === 'scale' ? [0.5, 0.5] : [20, 10],
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
    }),
    [resizeMode, scale, onResize, attachment, handleDragStart, handleDragEnd, isRadio],
  )

  return (
    <Box
      borderRadius={4}
      onClick={e => {
        e.stopPropagation()
        onAttachmentSelect(attachmentId)
      }}
      cursor="pointer"
      position="absolute"
      transition="background-color 300ms"
      w="100%"
      h="100%">
      <Box w="100%" h="100%" position="relative">
        <Center w="100%" h="100%" userSelect="none" position="relative">
          {children}
          <Box
            zIndex={2}
            pointerEvents="auto"
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
          />
        </Center>
        {selected ? (
          <OrientationOverlay
            zoom={pageScale}
            position={placement.orientation.position}
            onMove={onMove}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            resize={resize}
            onDelete={() => deleteAttachment(attachmentId)}
          />
        ) : null}
      </Box>
    </Box>
  )
}

const InlinePlacementContainer = ({
  children,
  pageHeight,
  attachment,
  pageWidth,
  selected,
  pageScale,
}: PropsWithChildren<
  {
    attachment: TemplateAttachment<InlineAttachmentPlacement>
    selected: boolean
  } & ContainerProps
>) => {
  // const editMode = useEditMode()
  const { editable } = usePdfView()
  const { placement } = attachment
  const { orientation } = placement
  const { position, scale } = orientation
  const [isDragging, setIsDragging] = useState(false)
  const [scaleX, scaleY] = scale
  const { x, y, width, height } = useMemo(() => {
    const [baseW, baseH] =
      attachment.type === 'data'
        ? [attachment.width, attachment.height]
        : getInlineFieldSize(attachment)
    const safeBaseW = Math.max(baseW, 1)
    const safeBaseH = Math.max(baseH, 1)
    const w = safeBaseW * pageScale
    const h = safeBaseH * pageScale

    return {
      width: w,
      height: h,
      x: position[0] * pageScale,
      y: position[1] * pageScale,
    }
  }, [position, attachment, pageScale])

  // Safety checks: ensure scale values are valid for framer motion
  const safeScaleX = Math.max(scaleX, 0.001)
  const safeScaleY = Math.max(scaleY, 0.001)

  return (
    <MCenter
      position="absolute"
      zIndex={isDragging ? 4 : attachment.type === 'shape' ? 2 : 5}
      initial={{
        opacity: 0,
        scaleX: safeScaleX * 0.9,
        scaleY: safeScaleY * 0.9,
      }}
      style={{
        transformOrigin: '0 0',
      }}
      key={attachment.id}
      animate={{ opacity: 1, scaleX: safeScaleX, scaleY: safeScaleY }}
      exit={{ opacity: 0, scaleX: safeScaleX * 0.9, scaleY: safeScaleY * 0.9 }}
      transformTemplate={({ scaleX, scaleY }) =>
        `scale(${scaleX}, ${scaleY}) translate(-50%, -50%)`
      }
      w={`${width}px`}
      top={`${y}px`}
      left={`${x}px`}
      h={`${height}px`}>
      {editable ? (
        <InlinePlacementEdit
          pageHeight={pageHeight}
          pageWidth={pageWidth}
          pageScale={pageScale}
          attachment={attachment}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          selected={selected}>
          {children}
        </InlinePlacementEdit>
      ) : (
        children
      )}
    </MCenter>
  )
}

const AttachmentPlacementContainer = ({
  attachment,
  pageHeight,
  pageWidth,
  pageScale,
  children,
}: PropsWithChildren<{ attachment: TemplateAttachment } & ContainerProps>) => {
  const { selectedAttachmentId } = useExternalPdfTemplateEditor()
  const { id: attachmentId, placement } = attachment
  const selected = useMemo(
    () => selectedAttachmentId === attachmentId,
    [selectedAttachmentId, attachmentId],
  )
  if (placement.type === 'inline')
    return (
      <InlinePlacementContainer
        selected={selected}
        attachment={attachment as TemplateAttachment<InlineAttachmentPlacement>}
        pageHeight={pageHeight}
        pageWidth={pageWidth}
        pageScale={pageScale}>
        {children}
      </InlinePlacementContainer>
    )

  return (
    <Center width={`${pageWidth}px`} height={`${pageHeight}px`}>
      {children}
    </Center>
  )
}
const InputAttachmentView = ({ attachment }: { attachment: InputTemplateAttachment }) => {
  const { onAttachmentSelect } = useExternalPdfTemplateEditor()
  const { id: attachmentId, field } = attachment
  // Pin the input to its per-field-type frozen style so global field-style changes don't shift
  // attachment rendering (and force templates to be re-adjusted). See attachmentFieldStyles.
  const { size, inputStyle, style } = getAttachmentFieldStyle(field.type)
  // Radio inputs size from their editable font size (which drives the radio control + option text)
  // rather than a pinned value.
  const resolvedSize =
    field.type === FieldTypes.DROPDOWN ? inputSizeFromFontSize(attachment.fontSize) : size
  // const selected = useMemo(
  //   () => selectedAttachmentId === attachmentId,
  //   [selectedAttachmentId, attachmentId],
  // )
  return (
    <TemplateFieldElementView
      style={{ ...defaultAttachmentContainerStyle, ...style }}
      size={resolvedSize}
      inputStyle={inputStyle}
      onSelect={() => onAttachmentSelect(attachmentId)}
      field={field}
      name={attachmentId}
    />
  )
}

const FileAttachmentView = ({ attachment }: { attachment: FileTemplateAttachment }) => (
  <Box>{attachment.file.name}</Box>
)

const DashedBorder = () => (
  <Box
    position="absolute"
    top={0}
    left={0}
    width="100%"
    height="100%"
    border="1px dashed #00000055"
    pointerEvents="none"
    zIndex={1}
  />
)

const DataAttachmentImage = ({
  value,
  editMode,
  width,
  height,
}: {
  value: FileDBValue | null
  editMode: EditMode
  width: number
  height: number
}) => {
  const { downloadPath, recentlyUploaded } = useFilePath(value)
  const fileArgs = useMemo<UseFileData>(
    () => ({
      path: downloadPath,
      pathLoading: recentlyUploaded,
      value,
    }),
    [downloadPath, recentlyUploaded, value],
  )
  const { url, loading } = useFile(fileArgs)
  const placeholderImage = useMemo(() => {
    if (loading) return imageIcon
    return editMode === 'template' ? imageIcon : errorIcon
  }, [editMode, loading])
  return (
    <Center
      border={`1px dashed ${editMode === 'document' ? 'transparent' : '#00000055'}`}
      boxSizing="content-box"
      w="100%"
      h="100%">
      <LoadingImage
        src={url}
        draggable={false}
        loading={loading}
        objectFit="contain"
        height={height}
        width={width}
        placeholderSrc={placeholderImage}
      />
      {editMode === 'template' ? null : <DashedBorder />}
    </Center>
  )
}

export const DataAttachmentText = ({
  value,
  shortcut,
  editMode,
  pageScale,
}: {
  value: string | FileDBValue | null
  shortcut: AttachmentShortcut
  editMode: EditMode
  pageScale: number
}) => {
  const { type: fieldType, name, style } = shortcut
  const center = fieldType === FieldTypes.FILE || fieldType === FieldTypes.SIGNATURE
  const Container = center ? Center : Flex
  const text = typeof value === 'string' ? value : (value?.name ?? 'None')
  // Match the generated PDF: the stored size (rem) maps to points via PIXEL_PDF_UNITS_SCALE on the
  // backend, and the attachment box scales with pageScale — scale the text the same way so the
  // overlay stays WYSIWYG at any view width
  const fontSizePx = (style?.font?.size ?? 1) * 16 * PIXEL_PDF_UNITS_SCALE * pageScale
  return (
    <Container w="100%" h="100%" display="flex" alignItems="center" bg={style?.backgroundColor}>
      <Text
        w="100%"
        isTruncated={fieldType !== FieldTypes.TEXTAREA}
        textAlign={center ? 'center' : 'left'}
        // lineHeight={1.2}
        whiteSpace={fieldType === FieldTypes.TEXTAREA ? 'pre-wrap' : 'nowrap'}
        color={style?.font?.color}
        fontFamily={style?.font?.family ? fontFamilyFromFontId(style.font.family) : undefined}
        fontSize={`${fontSizePx}px`}
        fontWeight={style?.font?.bold ? 700 : undefined}
        fontStyle={style?.font?.italic ? 'italic' : undefined}
        textDecoration={style?.font?.underline ? 'underline' : undefined}>
        {editMode === 'template' ? name : text}
      </Text>
      {editMode === 'template' ? <DashedBorder /> : null}
    </Container>
  )
}
const DataAttachmentView = ({
  attachment: { shortcut: data, value: populatedValue, width, height },
  pageScale,
}: {
  attachment: DataTemplateAttachment
  pageScale: number
}) => {
  const { id, path, type } = data
  const { shortcutArgs } = useTemplateData()
  const editMode = useEditMode()
  const value =
    populatedValue !== undefined
      ? populatedValue
      : (getShortcutValue(id, path, shortcutArgs) ?? null)
  if (type === FieldTypes.FILE && typeof value !== 'string') {
    return <DataAttachmentImage value={value} editMode={editMode} width={width} height={height} />
  }
  return (
    <DataAttachmentText value={value} shortcut={data} editMode={editMode} pageScale={pageScale} />
  )
}

const ShapeAttachmentView = ({
  attachment: { shapeType, fillColor, borderColor, borderWidth },
}: {
  attachment: ShapeTemplateAttachment
}) => {
  const editMode = useEditMode()
  const isRound = shapeType === 'oval' || shapeType === 'circle'
  const border = borderWidth && borderColor ? `${borderWidth}px solid ${borderColor}` : 'none'
  return (
    <Box
      w="100%"
      h="100%"
      bg={fillColor}
      borderRadius={isRound ? '50%' : undefined}
      boxSizing="content-box"
      border={border}>
      {editMode === 'template' ? <DashedBorder /> : null}
    </Box>
  )
}

const AttachmentViewBody = ({
  attachment,
  pageScale,
}: {
  attachment: TemplateAttachment
  pageScale: number
}) => {
  switch (attachment.type) {
    case 'input':
      return <InputAttachmentView attachment={attachment} />
    case 'file':
      return <FileAttachmentView attachment={attachment} />
    case 'data':
      return <DataAttachmentView attachment={attachment} pageScale={pageScale} />
    case 'shape':
      return <ShapeAttachmentView attachment={attachment} />
    default:
      throw new Error(`Unknown attachment type: ${(attachment as any).type}`)
  }
}

export const AttachmentView = ({
  attachment,
  ...containerProps
}: {
  attachment: TemplateAttachment
  pageWidth: number
  pageHeight: number
  pageScale: number
}) => (
  <AttachmentPlacementContainer {...containerProps} attachment={attachment}>
    <AttachmentViewBody attachment={attachment} pageScale={containerProps.pageScale} />
  </AttachmentPlacementContainer>
)
